import { useState } from "react";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES, ITEMS_PER_PAGE } from "../../../constant/constant";
import { useAxios } from "../../../utils/useAxios";

/**
 * @typedef {Object} ImportLogRow
 * @property {number} id
 * @property {number} import_log_id
 * @property {number} row_number
 * @property {"success"|"failed"} status
 * @property {string|null} message - Error message if failed, null if success
 * @property {Object} data - The imported row data (fields vary based on student/teacher)
 * @property {string} created_at
 * @property {string} updated_at
 */

/**
 * @typedef {import("../../../common/types/types").PaginationMeta} PaginationMeta
 */

/**
 * @typedef {Object} GetImportLogRowParams
 * @property {number} [page] - Page number for pagination
 * @property {number} [per_page] - Number of items per page
 * @property {"failed"|"success"} [status] - Filter by row status
 */

/**
 * Custom hook to fetch the row-level details of an import log by its ID
 * with pagination. Used by school admin.
 */
export const useGetImportLogRowDetails = () => {
  const [rows, setRows] = useState(/** @type {ImportLogRow[] | null} */(null));
  const [meta, setMeta] = useState(/** @type {PaginationMeta | null} */(null));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const { axiosInstance } = useAxios();

  /**
   * Fetch import log rows for a given log ID
   * @async
   * @param {number|string} logId - The import log ID
   * @param {GetImportLogRowParams} [params]
   * @returns {Promise<void>}
   */
  const getImportLogRows = async (
    logId,
    { page = 1, per_page = ITEMS_PER_PAGE, status } = {},
  ) => {
    try {
      setLoading(true);
      setError("");

      const query = new URLSearchParams();
      query.append("page", page);
      query.append("per_page", per_page);
      if (status) {
        query.append("filter[status]", status);
      }

      const url = `${TENANT_DOMAIN}/import/logs/${logId}/rows?${query.toString()}`;

      const response = await axiosInstance.get(url);

      setRows(response.data.data || []);
      setMeta({
        current_page: response.data.current_page,
        last_page: response.data.last_page,
        per_page: response.data.per_page,
        total: response.data.total,
        from: response.data.from,
        to: response.data.to,
      });
    } catch (err) {
      setRows(null);
      setMeta(null);

      if (err?.response?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    rows,
    meta,
    loading,
    error,
    getImportLogRows,
  };
};

